/**
 * Centralized constants for the application
 *
 * This file contains reusable constants for container widths, animation timings,
 * and other configuration values used across multiple components.
 */

/**
 * Container width mappings
 *
 * These classes define max-width constraints for content containers.
 * The `mx-auto` class is included to center the container.
 *
 * Usage:
 * - "full": No width constraint, spans full width
 * - "wide": Extra wide container (85rem = 1360px)
 * - "standard": Standard content width (6xl = 1152px)
 * - "narrow": Narrow content width (5xl = 1024px)
 * - "article": Article/reading width (3xl = 768px)
 */
export const containerWidths: Record<string, string> = {
  full: "w-full",
  wide: "max-w-[85rem] mx-auto",
  standard: "max-w-6xl mx-auto",
  narrow: "max-w-5xl mx-auto",
  article: "max-w-3xl mx-auto",
};

/**
 * Container width classes without centering (mx-auto)
 *
 * Use these when you need max-width constraints but want to control
 * positioning separately (e.g., with flex/grid parent).
 */
export const containerWidthsNoCenter: Record<string, string> = {
  full: "max-w-full",
  wide: "max-w-[85rem]",
  standard: "max-w-6xl",
  narrow: "max-w-5xl",
  article: "max-w-3xl",
};

/**
 * Get container width classes
 *
 * @param width - Width key (e.g., "full", "wide", "standard")
 * @param center - Whether to include mx-auto centering (default: true)
 * @returns Tailwind CSS classes for the container width
 *
 * @example
 * getContainerWidth("wide") // returns "max-w-[85rem] mx-auto"
 * getContainerWidth("wide", false) // returns "max-w-[85rem]"
 * getContainerWidth(null) // returns "max-w-6xl mx-auto" (standard)
 */
export const getContainerWidth = (
  width?: string | null,
  center: boolean = true
): string => {
  const widths = center ? containerWidths : containerWidthsNoCenter;
  const key = width || "standard";
  return widths[key] || widths.standard;
};

/**
 * Animation timing constants
 *
 * Use these for consistent animation delays across the application.
 * Values are in seconds.
 */
export const ANIMATION_DELAYS = {
  FAST: 0.1,
  MEDIUM: 0.2,
  SLOW: 0.3,
  EXTRA_SLOW: 0.4,
} as const;

/**
 * Default image sizes for Next.js Image component
 *
 * These responsive sizes tell the browser which image size to load
 * based on viewport width.
 */
export const DEFAULT_IMAGE_SIZES = {
  hero: "100vw",
  card: "(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw",
  full: "100vw",
  thumbnail: "(max-width: 768px) 50vw, 33vw",
} as const;
